/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Testimonial$1 as TypedTestimonial } from '@coursera-web/cds-labs';
import {
  TESTIMONIAL_DISPLAY_STYLES,
  TestimonialItem,
  TestimonialsModule,
  getDefaultTestimonials,
} from '@coursera-web/cds-labs';

import { breakpoints, typography2 } from '@coursera/cds-core';

import GrowthDiscovery from 'bundles/epic/clients/GrowthDiscovery';
import CustomTestimonialItem from 'bundles/unified-common/modules/testimonials/CustomTestimonialItem';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData:
    | ConsumerSpecializationPageFieldsFragment
    | EnterpriseSpecializationPageFieldsFragment
    | ConsumerCoursePageFieldsFragment
    | EnterpriseCoursePageFieldsFragment;
};

const styles = {
  /* 16px is not a variable because it represents the height of the slider navigation and not spacing */
  overrides: css`
    margin-bottom: calc(var(--cds-spacing-400) + 16px);

    ${breakpoints.down('sm')} {
      margin-bottom: calc(var(--cds-spacing-200) + 16px);
    }

    h2 {
      ${typography2.subtitleLarge}
      margin-bottom: var(--cds-spacing-150);
      margin-top: 0;
    }
  `,
  /* 16px is not a variable because it represents the height of the slider navigation and not spacing */
  overridesCarousel: css`
    margin-bottom: calc(var(--cds-spacing-400) + 16px);

    ${breakpoints.down('sm')} {
      margin-bottom: calc(var(--cds-spacing-200) + 16px);
    }

    h2 {
      ${typography2.subtitleLarge}
      margin-bottom: calc((var(--cds-spacing-400) - var(--cds-spacing-150)) * -1);
      margin-top: 0;
    }
  `,
};

const TestimonialsModuleRenderer = ({ pageData }: Props) => {
  const hasCustomTestimonials = pageData.testimonials && pageData.testimonials.length > 0;
  const { navigationIdMap } = usePageInfo();
  const eventingProductData = useEventingProductData();
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;
  const courseraPlusOwnership = useCourseraPlusOwnership();
  const removeBottomPadding =
    enableXDPRedesign ||
    (!courseraPlusOwnership?.isSubscribedToCPlusOrLite && GrowthDiscovery.preview('enableCardTakeoverExperiment'));

  let testimonialItems;
  let displayStyle;
  let overrideStyles;
  if (enableXDPRedesign) {
    overrideStyles = hasCustomTestimonials ? styles.overridesCarousel : styles.overrides;
  }

  if (hasCustomTestimonials) {
    testimonialItems = (pageData.testimonials as TypedTestimonial[])
      ?.map((testimonial, index) => (
        <CustomTestimonialItem
          key={testimonial.id}
          authorName={testimonial.authorName}
          authorTitle={testimonial.authorTitle}
          quote={testimonial.quote}
          image={testimonial.image}
          authorOrganization={testimonial.authorOrganization}
          productName={testimonial.associatedProduct && getProductTitle(testimonial.associatedProduct)}
          videoEmbedUrl={testimonial.video?.url}
          videoPlaceholderImage={testimonial.video?.url ? testimonial.image : undefined}
          eventingData={eventingProductData}
          trackingIndex={index}
        />
      ))
      .filter(Boolean);
    displayStyle = TESTIMONIAL_DISPLAY_STYLES.CAROUSEL;
  } else {
    const defaultTestimonials = getDefaultTestimonials();
    testimonialItems = defaultTestimonials
      .map((testimonial, index) => (
        <TestimonialItem
          key={testimonial.name}
          name={testimonial.name}
          quote={testimonial.quote}
          imageSrc={testimonial.imageSrc}
          learnerSince={testimonial.learnerSince}
          eventingData={eventingProductData}
          trackingIndex={index}
        />
      ))
      .filter(Boolean);
    displayStyle = TESTIMONIAL_DISPLAY_STYLES.CARDS;
  }

  return (
    <Section anchorId={navigationIdMap?.TESTIMONIALS}>
      <div css={overrideStyles}>
        <TestimonialsModule
          headline={_t('Why people choose Coursera for their career')}
          testimonialItems={testimonialItems}
          displayStyle={displayStyle}
          removeBottomPadding={removeBottomPadding}
          removeTopPadding={enableXDPRedesign}
        />
      </div>
    </Section>
  );
};

export default TestimonialsModuleRenderer;
